import useCategoryData from "@app/data-hooks/use-category-data.ts";
import useEntriesData from "@app/data-hooks/use-entries-data.ts";
import useAuth from "@app/hooks/use-auth.ts";
import { entryOrderStore } from "@app/stores/entry-store.ts";
import { entryFiltersStore } from "@app/stores/filters.store.ts";
import { EntryTableTableDefinition } from "@app/table-definitions/entry-table.table-definition.tsx";
import { Button } from "@mui/material";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { ColumnSort } from "@tanstack/react-table";
import { DataTable } from "bi-lib";
import { useMemo, useState, useEffect } from "react";

interface Props {
    catId: number;
    onShowReceipt: (id: string) => void;
    selectedIds: string[];
    setSelectedIds: (selectedIds: string[]) => void;
    postingMode: boolean;
    rateType: "billing" | "cost" | "internal";
    selectAll: boolean; // New prop for global Select All
}

export default function EntryTable({
    catId,
    onShowReceipt,
    selectedIds,
    setSelectedIds,
    postingMode,
    rateType,
    selectAll,
}: Props) {
    const [sortBy, setSortBy] = useState<ColumnSort | undefined>({ id: "itemDate", desc: false });

    const filters = useStore(entryFiltersStore);
    const fullFilter = useMemo(() => ({ ...filters, catId }), [filters, catId]);

    const { isAdmin } = useAuth();

    const navigate = useNavigate();
    const location = useLocation();

    const { entriesSearchList, entryTotals, deleteEntry } = useEntriesData({
        filters: fullFilter,
    });

    const { categories } = useCategoryData();

    // Synchronize local selections with global selectAll
    useEffect(() => {
        if (selectAll && entriesSearchList.data) {
            const newSelectedIds = [
                ...new Set([...selectedIds, ...entriesSearchList.data.map((x) => x.entryId!)]),
            ];
            setSelectedIds(newSelectedIds);
        } else if (!selectAll) {
            const tableEntryIds = entriesSearchList.data?.map((x) => x.entryId!) ?? [];
            setSelectedIds(selectedIds.filter((id) => !tableEntryIds.includes(id)));
        }
    }, [selectAll, entriesSearchList.data, setSelectedIds]);

    const allSelected = useMemo(
        () =>
            (entriesSearchList.data?.every((x) => selectedIds.includes(x.entryId!)) ?? false) &&
            selectedIds.some((x) => entriesSearchList.data?.some((y) => y.entryId == x) ?? false),
        [selectedIds, entriesSearchList],
    );

    const multiStaff = useMemo(() => {
        if ((filters.staffId?.length ?? 0) == 0) {
            return true;
        }
        return filters.staffId!.split(",").length > 1;
    }, [filters.staffId]);

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedIds(selectedIds.filter((x) => !entriesSearchList.data?.some((y) => y.entryId == x)));
        } else {
            setSelectedIds([...new Set([...selectedIds, ...entriesSearchList.data!.map((x) => x.entryId!)])]);
        }
    };

    const toggleItem = (id: string) => {
        const tempSelectedIds = [...selectedIds];
        const index = tempSelectedIds.indexOf(id);
        if (index === -1) {
            tempSelectedIds.push(id);
        } else {
            tempSelectedIds.splice(index, 1);
        }
        setSelectedIds(tempSelectedIds);
    };

    const sortedData = useMemo(() => {
        if (entriesSearchList.data == undefined) {
            return [];
        }

        if (sortBy?.id == undefined) {
            return entriesSearchList.data;
        }

        return [
            ...entriesSearchList.data.sort((a, b) => {
                if (!Object.keys(a).includes(sortBy.id)) {
                    return 0;
                }

                const aVal = a[sortBy.id as keyof typeof a];
                const bVal = b[sortBy.id as keyof typeof b];

                if (aVal == bVal) {
                    return 0;
                }
                if (aVal == undefined) {
                    return 1;
                }
                if (bVal == undefined) {
                    return -1;
                }
                if (aVal < bVal) {
                    return sortBy.desc ? 1 : -1;
                }
                return sortBy.desc ? -1 : 1;
            }),
        ];
    }, [entriesSearchList.data, sortBy]);

    return (
        <>
            <DataTable
                rowsPerPageOptions={[5000]}
                label={categories.data?.find((x) => x.categoryId == catId)?.categoryDescription ?? "Entries"}
                actions={
                    <Link to={"/entry/create"} search={{ catId, from: location.href }}>
                        <Button variant={"contained"}>Add New</Button>
                    </Link>
                }
                columns={EntryTableTableDefinition({
                    catId: catId,
                    onEdit: (item) => {
                        entryOrderStore.setState(() => sortedData?.map((x) => x.entryId!));
                        void navigate({
                            to: "/entry/$id",
                            search: { catId, from: location.href },
                            params: { id: item.entryId! },
                        });
                    },
                    onDelete: (id) => deleteEntry.mutate(id),
                    postingMode: postingMode,
                    selectedIds: selectedIds,
                    onSelectionChanged: toggleItem,
                    allSelected: allSelected,
                    onToggleSelectAll: toggleSelectAll,
                    isAdmin: isAdmin,
                    onViewReceipt: (id) => onShowReceipt(id),
                    multiStaff: multiStaff,
                    rateType: rateType,
                    quantityTotal: entryTotals.data?.quantityTotal ?? 0,
                    billingAmountTotal: entryTotals.data?.billingAmountTotal ?? 0,
                    costAmountTotal: entryTotals.data?.costAmountTotal ?? 0,
                    internalAmountTotal: entryTotals.data?.internalAmountTotal ?? 0,
                    rowCount: entriesSearchList.data?.length ?? 0,
                })}
                data={sortedData ?? []}
                queryHook={entriesSearchList}
                manualPagination={{
                    pageIndex: 0,
                    onPageIndexChange: () => {},
                    rowsPerPage: 5000,
                    onRowsPerPageChange: () => {},
                    sortBy: sortBy == undefined ? [] : [sortBy],
                    onSortByChange: (value) => setSortBy(value[0]),
                    totalRowCount: entriesSearchList.data?.length ?? 0,
                }}
            />
        </>
    );
}
